/**
 * "Make it a story": the fan explicitly chooses one of THEIR takes from this room (or none),
 * publishes, then shares the consumer link `/story/:id` (never `/api`).
 */
import { useEffect, useState } from 'react';
import type { DirectorLanguage, DirectorResponse, Fan, PersonalStory, Take } from '@clutch/core';
import { useClient, store } from '../client';
import { DirectorBusyError, DirectorHttpError, DirectorUnavailableError, storyShareUrl } from './api';
import type { Verdict } from './perspective';
import { htmlLang, pick } from './i18n';

const MINE_KEY = 'clutch.myStories';
/** Stories this browser published: the public page offers "Remove story" to its owner. */
export function rememberStory(storyId: string, fanId: string) {
  const all = ownStories();
  all[storyId] = fanId;
  store.set(MINE_KEY, JSON.stringify(all));
}
export function ownStories(): Record<string, string> {
  try {
    const v = JSON.parse(store.get(MINE_KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}
export function forgetStory(storyId: string) {
  const all = ownStories();
  delete all[storyId];
  store.set(MINE_KEY, JSON.stringify(all));
}

const COPY = {
  en: { title: 'Make it a story', sub: 'Your verdict, the moments, the final. Add your reaction if you want.', none: 'No take', noneSub: 'Moments only', choose: 'Add one of your takes', noTakes: 'No takes from this room yet — that’s fine.', publish: 'Publish story', publishing: 'Publishing…', ready: 'Your story is live', share: 'Share', copy: 'Copy link', copied: 'Copied', open: 'Open story', close: 'Done', error: 'Couldn’t publish. Try again.', busy: 'Busy right now — try again in a few seconds.', note: 'Anyone with the link can watch. You can remove it any time.' },
  hi: { title: 'इसे स्टोरी बनाइए', sub: 'आपका नतीजा, अहम पल, अंतिम स्कोर। चाहें तो अपना रिएक्शन जोड़ें।', none: 'कोई टेक नहीं', noneSub: 'सिर्फ़ पल', choose: 'अपना एक टेक जोड़ें', noTakes: 'इस रूम से अभी कोई टेक नहीं — कोई बात नहीं।', publish: 'स्टोरी पब्लिश करें', publishing: 'पब्लिश हो रही है…', ready: 'आपकी स्टोरी लाइव है', share: 'शेयर', copy: 'लिंक कॉपी करें', copied: 'कॉपी हो गया', open: 'स्टोरी खोलें', close: 'हो गया', error: 'पब्लिश नहीं हो सका। फिर कोशिश करें।', busy: 'अभी व्यस्त — कुछ सेकंड में फिर कोशिश करें।', note: 'लिंक वाला कोई भी देख सकता है। आप इसे कभी भी हटा सकते हैं।' },
  'zh-Hans': { title: '生成你的故事', sub: '你的观点、关键时刻与比分。也可以加入你的反应。', none: '不添加录像', noneSub: '只分享比赛时刻', choose: '添加一段你的录像', noTakes: '你还没有在这个房间录制，不添加也可以。', publish: '发布故事', publishing: '正在发布…', ready: '你的故事已发布', share: '分享', copy: '复制链接', copied: '已复制', open: '打开故事', close: '完成', error: '发布失败，请重试。', busy: '目前繁忙，请稍后重试。', note: '任何拥有链接的人都能观看。你随时可以删除故事。' },
};

export function PublishSheet({ director, fan, verdict, language, onClose, credit }: { director: DirectorResponse; fan: Fan; verdict: Verdict; language: DirectorLanguage; onClose: () => void; /** "Storyteller · Rachel": how the fan directed it. */ credit?: string }) {
  const client = useClient();
  const c = COPY[language];
  const [takes, setTakes] = useState<Take[]>();
  const [choice, setChoice] = useState<string>(); // undefined = no take
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const [story, setStory] = useState<PersonalStory>();
  const [copied, setCopied] = useState(false);
  const [narration, setNarration] = useState<'idle' | 'loading' | 'ready' | 'unavailable'>('idle');

  useEffect(() => {
    let alive = true;
    client.takes(director.roomId).then((all) => {
      if (!alive) return;
      const eligible = new Set(director.takeIds);
      setTakes(all.filter((t) => t.fanId === fan.fanId && eligible.has(t.takeId)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    }).catch(() => alive && setTakes([]));
    return () => { alive = false; };
  }, [client, director, fan.fanId]);

  const publish = async () => {
    if (!client.director || busy) return;
    setBusy(true);
    setErr(undefined);
    try {
      const s = await client.director.publish({ fanId: fan.fanId, directorId: director.directorId, ...(choice ? { takeId: choice } : {}) });
      rememberStory(s.storyId, fan.fanId);
      setStory(s);
    } catch (e) {
      setErr(e instanceof DirectorBusyError ? c.busy
        : e instanceof DirectorUnavailableError && e.detail.code === 'DIRECTOR_VALIDATION_FAILED'
          ? pick({ en: 'Your original show is saved. The version for viewers didn’t pass the fact check. Retry to prepare a new shareable version.', hi: 'आपका मूल शो सुरक्षित है। दर्शकों के लिए बनाई गई कहानी की तथ्य-जाँच सफल नहीं हुई। शेयर करने वाला संस्करण फिर तैयार करें।', zh: '你的原始节目已保存。面向观众的版本未通过事实核查。请重试以准备新的分享版本。' }, language)
          : e instanceof DirectorUnavailableError
            ? pick({ en: 'Your original show is saved. The AI couldn’t prepare the shared version right now. Please retry.', hi: 'आपका मूल शो सुरक्षित है। AI अभी शेयर करने वाला संस्करण तैयार नहीं कर सका। फिर कोशिश करें।', zh: '你的原始节目已保存。AI暂时无法准备分享版本，请重试。' }, language)
            : e instanceof DirectorHttpError && e.status === 404
              ? pick({ en: 'This private show has expired. Close this sheet and create a fresh catch-up before publishing.', hi: 'इस निजी शो की समय-सीमा समाप्त हो गई है। पब्लिश करने से पहले नया कैच-अप बनाएँ।', zh: '此私人节目的保存期限已过。请关闭窗口并生成新的回顾后再发布。' }, language)
              : c.error);
    } finally {
      setBusy(false);
    }
  };

  const url = story ? storyShareUrl(location.origin, story) : '';
  const addNarration = async () => {
    if (!story || !client.director || narration === 'loading') return;
    setNarration('loading');
    try {
      const audio = await client.director.publishSpeech(story.storyId, fan.fanId);
      setNarration(audio.status === 'ready' ? 'ready' : 'unavailable');
    } catch { setNarration('unavailable'); }
  };
  const share = async () => {
    if (!story) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Clutch', text: verdict.headline, url });
        return;
      }
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return;
    }
    await copy();
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* the link is visible to select */
    }
  };

  return (
    <div className="sheet-scrim" role="dialog" aria-modal="true" aria-label={c.title} lang={htmlLang(language)} data-testid="publish-sheet" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <section className="pub">
        <span className="pub__grab" aria-hidden="true" />
        {!story ? (
          <>
            <h2 className="pub__title">{c.title}</h2>
            <p className="pub__sub">{director.asOf.status === 'final' ? c.sub : pick({ en: 'Save this moment in the game. Add your reaction if you want.', hi: 'मैच के इस पल को सहेजें। चाहें तो अपना रिएक्शन जोड़ें।', zh: '保存比赛的这一刻，也可以加入你的反应。' }, language)}</p>
            <div className={`pub__preview pub__preview--${verdict.tone}`}>
              <b>{verdict.headline}</b>
              <span>{director.game.away.tricode} {director.asOf.awayScore} – {director.asOf.homeScore} {director.game.home.tricode}</span>
            </div>
            {credit && <p className="pub__credit" data-testid="publish-credit">{pick({ en: 'Directed by you', hi: 'आपके निर्देशन में', zh: '由你执导' }, language)} · {credit}</p>}
            <p className="pub__label">{c.choose}</p>
            <div className="pub__takes" role="radiogroup" aria-label={c.choose}>
              <button role="radio" aria-checked={!choice} className={`pub__take pub__take--none${!choice ? ' is-on' : ''}`} onClick={() => setChoice(undefined)} data-testid="take-none">
                <span className="pub__none" aria-hidden="true">∅</span>
                <b>{c.none}</b>
                <small>{c.noneSub}</small>
              </button>
              {takes?.map((t) => (
                <button key={t.takeId} role="radio" aria-checked={choice === t.takeId} className={`pub__take${choice === t.takeId ? ' is-on' : ''}`} onClick={() => setChoice(t.takeId)} data-testid="take-option">
                  <video src={`${client.takeUrl(t)}#t=0.4`} preload="metadata" muted playsInline />
                  <b>{Math.round(t.durationMs / 1000)}s</b>
                  <small>{t.marks.length} {pick({ en: t.marks.length === 1 ? 'play' : 'plays', hi: 'प्ले', zh: '个回合' }, language)}</small>
                </button>
              ))}
            </div>
            {takes && takes.length === 0 && <p className="pub__hint">{c.noTakes}</p>}
            {err && <p className="alert" role="alert">{err}</p>}
            <button className="btn btn--primary btn--block pub__go" onClick={publish} disabled={busy} data-testid="publish-story">{busy ? c.publishing : c.publish}</button>
            {busy && <p className="pub__hint" role="status">{pick({ en: 'Preparing and checking the version for viewers. Your original show stays saved.', hi: 'दर्शकों के लिए संस्करण तैयार हो रहा है और तथ्य जाँचे जा रहे हैं। आपका मूल शो सुरक्षित है।', zh: '正在准备并核查面向观众的版本。你的原始节目仍已保存。' }, language)}</p>}
            <p className="pub__fine">{c.note}</p>
          </>
        ) : (
          <div className="pub__done" data-testid="share-sheet">
            <span className="pub__check" aria-hidden="true">✓</span>
            <h2 className="pub__title">{c.ready}</h2>
            <p className="pub__link" data-testid="story-link">{url}</p>
            {credit && <p className="pub__credit" data-testid="share-credit">{pick({ en: 'Directed by you', hi: 'आपके निर्देशन में', zh: '由你执导' }, language)} · {credit}</p>}
            <div className="pub__row">
              <button className="btn btn--primary" onClick={share} data-testid="share-story">{c.share}</button>
              <button className="btn btn--ghost" onClick={copy}>{copied ? c.copied : c.copy}</button>
            </div>
            <a className="btn btn--ghost btn--block" href={story.sharePath} data-testid="open-story">{c.open} ↗</a>
            <button className="btn btn--ghost btn--block" onClick={() => void addNarration()} disabled={narration === 'loading' || narration === 'ready'} data-testid="publish-narration">
              {pick({ hi: narration === 'loading' ? 'आवाज़ तैयार हो रही है…' : narration === 'ready' ? 'शेयर की गई कहानी की आवाज़ तैयार है' : 'शेयर की गई कहानी में AI आवाज़ जोड़ें',
                en: narration === 'loading' ? 'Preparing narration…' : narration === 'ready' ? 'Shared narration ready' : 'Add AI narration for viewers',
                zh: narration === 'loading' ? '正在准备旁白…' : narration === 'ready' ? '分享旁白已就绪' : '为观众添加AI旁白' }, language)}
            </button>
            {narration === 'unavailable' && <p role="status">{pick({ en: 'Narration is unavailable. Your story still works with captions.', hi: 'अभी आवाज़ उपलब्ध नहीं है। आपकी कहानी कैप्शन के साथ चलती रहेगी।', zh: '语音暂不可用。仍可通过字幕观看故事。' }, language)}</p>}
            <button className="pub__close" onClick={onClose}>{c.close}</button>
          </div>
        )}
      </section>
    </div>
  );
}
